/*
 * Fish & Friends Tees — image-to-color sync
 * When a shopper selects a mockup in the gallery (thumbnail click, or carousel
 * swipe / prev-next) and that mockup is the featured image of a color variant,
 * select that color automatically. Reuses Dawn's native variant flow by
 * dispatching a bubbling 'change' on the matching option input, so price,
 * availability, URL and image all update through the normal path.
 *
 * Loop guard: a variant change re-renders media and can re-emit slideChanged;
 * selectColorForMedia is a no-op when the target color is already selected, so
 * the cycle can't close. Scoped to its own <product-info>, so quick-add and
 * featured-product blocks are unaffected.
 *
 * One mockup per color today. If a color ever gets a second image (e.g. hoodies
 * front/back), only its featured_media auto-selects; extra images are harmless
 * no-ops because they aren't in the map.
 */
if (!customElements.get('media-variant-sync')) {
  customElements.define(
    'media-variant-sync',
    class MediaVariantSync extends HTMLElement {
      connectedCallback() {
        const mapEl = this.querySelector('[data-media-color-map]');
        try {
          this.map = mapEl ? JSON.parse(mapEl.textContent) : null;
        } catch (e) {
          this.map = null;
        }
        if (!this.map || Object.keys(this.map).length === 0) return;

        this.productInfo = this.closest('product-info');
        const gallery = this.productInfo?.querySelector('media-gallery');
        if (!gallery) return;

        // Keep the carousel order stable: tell the gallery / product-info not to
        // reorder slides on variant change, so swiping doesn't yank the selected
        // mockup to the front and snap the slider back. Safe here: the product
        // template shows all media (hide_variants is off).
        gallery.dataset.stableOrder = 'true';

        // Thumbnail clicks: select the color immediately
        gallery.querySelectorAll('[id^="GalleryThumbnails"] li[data-target]').forEach((li) => {
          li.querySelector('button')?.addEventListener('click', () => this.selectColorForMedia(li.dataset.target));
        });

        // Main carousel slide change (swipe or prev/next): debounce so the variant
        // change fires after the slide settles, not mid-gesture.
        const viewer = gallery.querySelector('[id^="GalleryViewer"]');
        let slideTimer;
        viewer?.addEventListener('slideChanged', (event) => {
          const mediaId = event.detail?.currentElement?.dataset?.mediaId;
          if (!mediaId) return;
          clearTimeout(slideTimer);
          slideTimer = setTimeout(() => this.selectColorForMedia(mediaId), 300);
        });
      }

      selectColorForMedia(mediaKey) {
        const valueId = mediaKey && this.map ? this.map[mediaKey] : null;
        if (!valueId) return; // image not linked to a color variant

        const variantSelects = this.productInfo?.querySelector('variant-selects');
        const input = variantSelects?.querySelector(`[data-option-value-id="${valueId}"]`);
        if (!input) return;

        const isOption = input.tagName === 'OPTION';
        if (isOption ? input.selected : input.checked) return; // already selected: no-op (loop guard)

        if (isOption) {
          input.selected = true;
          input.closest('select')?.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  );
}
